import { useEffect, useRef } from 'react'
import * as PIXI from 'pixi.js'
import { getPlantSegments } from '../utils/lSystem'
import { getGradientColor, adjustColorLuminance } from '../utils/colorUtils'
import { applyStemTexture } from '../utils/plantVisuals'

const alignToPixel = (value) => Math.round(value)
const alignPoint = (x, y) => ({
  x: alignToPixel(x),
  y: alignToPixel(y),
})

function PlantPreview({ plantData, size = 300 }) {
  const canvasRef = useRef(null)
  const appRef = useRef(null)

  useEffect(() => {
    if (!canvasRef.current || !plantData) return

    // Create PixiJS application
    const app = new PIXI.Application({
      width: size,
      height: size,
      backgroundColor: 0x87CEEB,
      antialias: true,
    })

    canvasRef.current.appendChild(app.view)
    appRef.current = app

    // Draw from saved data (single plant or garden snapshot)
    if (plantData.plant_data) {
      const snapshot = plantData.plant_data

      const renderSinglePlant = (plant, container) => {
        drawPlant(container, plant, size)
      }

      if (Array.isArray(snapshot.plants) && snapshot.plants.length > 0) {
        const visiblePlants = snapshot.plants.filter((p) => p && p.config)
        if (!visiblePlants.length) {
          return () => {
            app.destroy(true, { children: true, texture: true })
          }
        }

        const baselineY = size - 50
        const paddingX = 30
        const availableWidth = Math.max(1, size - paddingX * 2)
        const slotWidth = availableWidth / visiblePlants.length

        visiblePlants.forEach((plant, index) => {
          const plantContainer = new PIXI.Container()
          const slotCenter = paddingX + slotWidth * (index + 0.5)
          plantContainer.position.set(slotCenter, baselineY)
          app.stage.addChild(plantContainer)
          renderSinglePlant(plant, plantContainer)
        })
      } else {
        const plantContainer = new PIXI.Container()
        plantContainer.position.set(size / 2, size - 50)
        app.stage.addChild(plantContainer)
        renderSinglePlant(snapshot, plantContainer)
      }
    }

    return () => {
      app.destroy(true, { children: true, texture: true })
    }
  }, [plantData, size])

  return (
    <div 
      ref={canvasRef} 
      className="pixel-art"
      style={{ width: size, height: size }}
    />
  )
}

function drawPlant(container, plantData, canvasSize) {
  const pixelSize = 4
  const progress = plantData.progress || 0
  let segments = getPlantSegments(plantData, progress, { pixelSize })
  const plantConfig = plantData.config || {}

  if (!segments.length) {
    return
  }

  const bounds = segments.reduce(
    (acc, segment) => ({
      minX: Math.min(acc.minX, segment.x1, segment.x2),
      maxX: Math.max(acc.maxX, segment.x1, segment.x2),
      minY: Math.min(acc.minY, segment.y1, segment.y2),
      maxY: Math.max(acc.maxY, segment.y1, segment.y2),
    }),
    {
      minX: Infinity,
      maxX: -Infinity,
      minY: Infinity,
      maxY: -Infinity,
    },
  )

  const totalWidth = Math.max(1, bounds.maxX - bounds.minX)
  const totalHeight = Math.max(1, bounds.maxY - bounds.minY)
  const availableWidth = Math.max(1, (canvasSize - 60) / pixelSize)
  const availableHeight = Math.max(1, (canvasSize - 100) / pixelSize)
  const scale = Math.min(1, availableWidth / totalWidth, availableHeight / totalHeight)

  const centerX = (bounds.minX + bounds.maxX) / 2
  const baseY = bounds.maxY

  const normalizedSegments = segments.map((segment) => ({
    ...segment,
    x1: (segment.x1 - centerX) * scale,
    y1: (segment.y1 - baseY) * scale,
    x2: (segment.x2 - centerX) * scale,
    y2: (segment.y2 - baseY) * scale,
  }))

  let lastSegment = null
  normalizedSegments.forEach((segment) => {
    lastSegment = drawSegment(container, segment, plantConfig, pixelSize)
  })

  const totalClicks = (plantConfig.clicksPerStage || 10) * (plantConfig.stages || 10)
  if (progress >= totalClicks && lastSegment) {
    drawPixelFlower(container, lastSegment.x2 * pixelSize, lastSegment.y2 * pixelSize, pixelSize, plantConfig)
  }
}

function drawSegment(container, segment, plantConfig, pixelSize) {
  const color = getGradientColor(plantConfig, segment.progressRatio)
  const thickness = Math.max(2, Math.round(pixelSize * segment.thicknessFactor))

  const start = alignPoint(segment.x1 * pixelSize, segment.y1 * pixelSize)
  const end = alignPoint(segment.x2 * pixelSize, segment.y2 * pixelSize)
  const minY = Math.min(start.y, end.y)
  const height = Math.max(thickness, Math.abs(end.y - start.y)) || thickness

  const pixel = new PIXI.Graphics()
  pixel.beginFill(color)
  pixel.drawRect(start.x - thickness / 2, minY, thickness, height)
  pixel.endFill()

  const textureSeed = ((segment.x1 + segment.y1) * 0.23 + segment.depth * 0.17) % 1
  applyStemTexture(pixel, color, thickness, textureSeed)

  pixel.position.set(end.x, end.y)
  container.addChild(pixel)
  return segment
}

function drawPixelFlower(container, x, y, pixelSize, plantConfig) {
  const flowerColor = plantConfig?.flowerColor ? parseInt(plantConfig.flowerColor.replace('#', ''), 16) : 0xFFD700
  
  const center = new PIXI.Graphics()
  center.beginFill(flowerColor)
  center.drawRect(x - pixelSize, y - pixelSize, pixelSize * 2, pixelSize * 2)
  center.endFill()
  const aligned = alignPoint(x, y)
  center.position.set(aligned.x, aligned.y)
  container.addChild(center)
  
  const petalPositions = [
    [-2, 0], [2, 0], [0, -2], [0, 2],
    [-1, -1], [1, -1], [-1, 1], [1, 1]
  ]
  
  const r = (flowerColor >> 16) & 0xFF
  const g = (flowerColor >> 8) & 0xFF
  const b = flowerColor & 0xFF
  const petalColor = ((r * 0.8) << 16) | ((g * 0.8) << 8) | (b * 0.8)
  
  petalPositions.forEach(([dx, dy]) => {
    const petal = new PIXI.Graphics()
    petal.beginFill(petalColor)
    const leafX = x + (dx * pixelSize)
    const leafY = y + (dy * pixelSize)
    const aligned = alignPoint(leafX, leafY)
    petal.drawRect(aligned.x, aligned.y, pixelSize, pixelSize)
    petal.endFill()
    container.addChild(petal)
  })
}

export default PlantPreview
